import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../i18n";
import TickerStrip from "../components/TickerStrip";
import MarketPulse from "../components/MarketPulse";
import WeeklyDecision from "../components/WeeklyDecision";
import GoldoraStrengths from "../components/GoldoraStrengths";
import { ArrowUpRight, Globe, LineChart, FlaskConical, MapPin, Bot, Layers, Sparkles, ShieldCheck, HeartPulse } from "lucide-react";

const HERO_BG =
  "https://images.pexels.com/photos/30547584/pexels-photo-30547584.jpeg";

const bentoItems = (t) => [
  {
    to: "/mercado",
    icon: Globe,
    title: t.bento.market_t,
    desc: t.bento.market_d,
    testid: "bento-market",
    span: "md:col-span-7 md:row-span-2",
    accent: "from-gold-500/20 to-transparent",
  },
  {
    to: "/comparador",
    icon: Layers,
    title: t.bento.compare_t,
    desc: t.bento.compare_d,
    testid: "bento-comparator",
    span: "md:col-span-5",
    accent: "from-gold-700/25 to-transparent",
  },
  {
    to: "/simulador",
    icon: FlaskConical,
    title: t.bento.sim_t,
    desc: t.bento.sim_d,
    testid: "bento-simulator",
    span: "md:col-span-5",
    accent: "from-sky-500/10 to-transparent",
  },
  {
    to: "/geografia",
    icon: MapPin,
    title: t.bento.geo_t,
    desc: t.bento.geo_d,
    testid: "bento-geography",
    span: "md:col-span-4 md:row-span-2",
    accent: "from-violet-500/12 to-transparent",
  },
  {
    to: "/assistente",
    icon: Bot,
    title: t.bento.ai_t,
    desc: t.bento.ai_d,
    testid: "bento-ai",
    span: "md:col-span-5",
    accent: "from-gold-400/18 to-transparent",
  },
  {
    to: "/coach",
    icon: HeartPulse,
    title: t.bento.coach_t,
    desc: t.bento.coach_d,
    testid: "bento-coach",
    span: "md:col-span-4",
    accent: "from-emerald-500/15 to-transparent",
  },
  {
    to: "/perfil",
    icon: ShieldCheck,
    title: t.bento.profile_t,
    desc: t.bento.profile_d,
    testid: "bento-profile",
    span: "md:col-span-3",
    accent: "from-pink-500/10 to-transparent",
  },
];

export default function DecisionHub() {
  const { t, lang } = useI18n();
  const items = bentoItems(t);

  return (
    <div data-testid="page-decision-hub">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 opacity-50"
          style={{
            background: `linear-gradient(180deg, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.95) 70%, #000000 100%), url(${HERO_BG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 grain" aria-hidden />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-36 pb-20">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-xs uppercase tracking-[0.2em] text-gold-400 font-mono rise-in">
            <Sparkles size={12} />
            {t.hero.kicker}
          </div>
          <h1
            className="mt-6 text-5xl sm:text-6xl lg:text-7xl font-display font-medium tracking-tight leading-[1.02] max-w-4xl rise-in"
            data-testid="hero-title"
          >
            {t.hero.title1}{" "}
            <span className="italic text-gold-400">{t.hero.title2}</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-neutral-400 leading-relaxed rise-in" style={{ animationDelay: "120ms" }}>
            {t.hero.subtitle}
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-3 rise-in" style={{ animationDelay: "220ms" }}>
            <Link
              to="/mercado"
              data-testid="hero-cta-primary"
              className="group relative inline-flex items-center gap-2 bg-gold-500 hover:bg-gold-400 text-black font-medium px-5 py-3 rounded-md transition-colors"
            >
              {t.hero.cta_primary}
              <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
            <Link
              to="/assistente"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white px-5 py-3 rounded-md transition-colors"
            >
              <Bot size={16} />
              {t.hero.cta_secondary}
            </Link>
          </div>
          <p className="mt-8 text-xs text-neutral-600 font-mono uppercase tracking-[0.2em]">
            {t.hero.disclaimer}
          </p>
        </div>
      </section>

      <MarketPulse />
      <TickerStrip />

      <WeeklyDecision />

      {/* GOLDORA DIFFERENTIATORS — reminder of why the user is here */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-10 pb-4" data-testid="home-strengths">
        <div className="surface p-6 sm:p-8">
          <GoldoraStrengths />
        </div>
      </section>

      {/* BENTO */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 auto-rows-[160px] md:auto-rows-[170px] gap-4">
          {items.map((item, idx) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={item.testid}
                className={`relative group surface ${item.span} overflow-hidden rise-in hover:border-white/20 transition-all`}
                style={{ animationDelay: `${idx * 90 + 200}ms` }}
              >
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${item.accent} opacity-80 group-hover:opacity-100 transition-opacity`}
                  aria-hidden
                />
                <div className="relative h-full p-6 flex flex-col justify-between">
                  <div className="flex items-start justify-between">
                    <div className="w-10 h-10 rounded-md bg-white/5 border border-white/10 grid place-items-center text-gold-400 group-hover:text-gold-300 group-hover:bg-gold-500/10 transition-colors">
                      <Icon size={18} />
                    </div>
                    <ArrowUpRight
                      size={18}
                      className="text-neutral-600 group-hover:text-white group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all"
                    />
                  </div>
                  <div>
                    <h3 className="font-display text-xl sm:text-2xl text-white tracking-tight">{item.title}</h3>
                    <p className="mt-1.5 text-sm text-neutral-400 max-w-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              </Link>
            );
          })}

          {/* Categories strip */}
          <div
            className="relative surface md:col-span-12 overflow-hidden p-6"
            data-testid="bento-categories"
          >
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <span className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-mono">
                  {lang === "pt" ? "Cobertura" : "Coverage"}
                </span>
                <h3 className="mt-1 font-display text-2xl">{t.bento.cat_t}</h3>
              </div>
              <div className="flex flex-wrap gap-2 text-xs font-mono uppercase">
                {[
                  "Stocks", "ETFs", "Bonds", "Crypto", "Real Estate", "REITs", "Gold", "Forex", "Commodities", "Private Equity", "Derivatives", "Alternatives",
                ].map((c) => (
                  <span key={c} className="px-2.5 py-1 rounded border border-white/10 bg-white/[0.03] text-neutral-400">
                    {c}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY SECTION */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 border-t border-white/5">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <span className="text-xs uppercase tracking-[0.2em] text-gold-400 font-mono">
              {lang === "pt" ? "Diferencial" : "Differentiator"}
            </span>
            <h2 className="mt-3 font-display text-4xl lg:text-5xl tracking-tight">
              {lang === "pt"
                ? "Não vendemos investimento. Entregamos decisão."
                : "We don't sell investments. We deliver decisions."}
            </h2>
          </div>
          <div className="md:col-span-7 grid sm:grid-cols-2 gap-6">
            {[
              {
                t: lang === "pt" ? "Comparação cruzada" : "Cross-asset",
                d:
                  lang === "pt"
                    ? "Casa em Lisboa vs. Bitcoin vs. S&P 500 — lado a lado."
                    : "House in Lisbon vs. Bitcoin vs. S&P 500 — side by side.",
              },
              {
                t: lang === "pt" ? "Global" : "Global",
                d:
                  lang === "pt"
                    ? "11 moedas, 12 países, 5 regiões cobertas."
                    : "11 currencies, 12 countries, 5 regions covered.",
              },
              {
                t: lang === "pt" ? "Decisão-first" : "Decision-first",
                d:
                  lang === "pt"
                    ? "Todo gráfico termina com um 'takeaway' em português claro."
                    : "Every chart ends with a plain-English takeaway.",
              },
              {
                t: lang === "pt" ? "Sem conflito" : "No conflicts",
                d:
                  lang === "pt"
                    ? "Não vendemos produto financeiro. Você decide sozinho."
                    : "We don't sell financial products. You decide on your own.",
              },
            ].map((f) => (
              <div key={f.t} className="surface p-6">
                <div className="text-gold-400 font-mono text-xs uppercase tracking-[0.18em]">{f.t}</div>
                <div className="mt-2 text-white text-lg leading-snug font-display">{f.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
